/**
 * 上下文与消息的单调版本比较。
 *
 * 约束来源：CONTRACT.md 第 30 行 "conditional monotonic VERSION not collectedAt"、
 * 需求文档第五节 "基于 pageInstanceId 和单调版本处理更新，不能只依靠客户端时间排序"。
 *
 * 因此版本只使用十进制字符串并做数值比较；到达时间更晚但版本更小的上报必须被丢弃。
 */

import { compareDecimal, isCanonicalDecimal } from './ids';

export interface VersionedUpdate<T> {
  /** 上报到达时间（本地时钟），仅用于展示，不参与先后判定。 */
  receivedAt: number;
  /** 单调版本，十进制字符串。 */
  version: string;
  value: T;
}

export type VersionDecision =
  | { accepted: false; reason: 'equal-version' | 'stale-version' }
  | { accepted: true; version: string };

/** 版本自增：每次客户端产生新的上下文状态时调用。 */
export function nextVersion(current: null | string): string {
  if (current === null) return '1';
  if (!isCanonicalDecimal(current)) {
    throw new TypeError(`版本必须是规范化十进制字符串: ${JSON.stringify(current)}`);
  }
  return (BigInt(current) + 1n).toString();
}

/**
 * 判定一次上报是否应当被接受：
 * 只有严格更大的版本才接受；相同版本视为幂等重复（不覆盖），更小版本视为延迟旧报丢弃。
 */
export function decideVersion(
  current: null | string,
  incoming: string,
): VersionDecision {
  if (!isCanonicalDecimal(incoming)) {
    throw new TypeError(`版本必须是规范化十进制字符串: ${JSON.stringify(incoming)}`);
  }
  if (current === null) return { accepted: true, version: incoming };
  if (!isCanonicalDecimal(current)) {
    throw new TypeError(`版本必须是规范化十进制字符串: ${JSON.stringify(current)}`);
  }
  const cmp = compareDecimal(incoming, current);
  if (cmp === 0) return { accepted: false, reason: 'equal-version' };
  if (cmp < 0) return { accepted: false, reason: 'stale-version' };
  return { accepted: true, version: incoming };
}

/**
 * 按 pageInstanceId 维护的"最近上报上下文"注册表。
 * 每个页面实例独立一份，绝不共享单一全局 latestContext。
 */
export class LatestContextRegistry<T> {
  private readonly entries = new Map<string, VersionedUpdate<T>>();

  /** 应用一次上报，返回是否被接受（旧版本/同版本会被拒绝）。 */
  apply(pageInstanceId: string, version: string, value: T, now: number): VersionDecision {
    const existing = this.entries.get(pageInstanceId);
    const decision = decideVersion(existing?.version ?? null, version);
    if (!decision.accepted) return decision;
    this.entries.set(pageInstanceId, { receivedAt: now, value, version });
    return decision;
  }

  get(pageInstanceId: string): undefined | VersionedUpdate<T> {
    return this.entries.get(pageInstanceId);
  }

  /** 所有页面实例的最新状态（工作台右侧"最近上报"展示用）。 */
  all(): Array<{ pageInstanceId: string; update: VersionedUpdate<T> }> {
    return [...this.entries.entries()].map(([pageInstanceId, update]) => ({
      pageInstanceId,
      update,
    }));
  }

  clear(): void {
    this.entries.clear();
  }
}